import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  UserCheck,
  UserMinus,
  UserPlus,
  Clock,
  ArrowRight,
  Bell,
  ExternalLink,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Link } from "wouter";
import { usePermissions } from "@/hooks/usePermissions";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface EligibilityChangeEvent {
  id: number;
  employeeId: number;
  clientId: number;
  planId: number | null;
  eventType: string;
  employeeName: string;
  clientName: string;
  effectiveDate: string | null;
  enrollmentDueDate: string | null;
  employmentType: string | null;
  previousEmploymentType: string | null;
  isEligible: boolean | null;
  notes: string | null;
  createdAt: string;
}

interface EligibilityChangesResponse {
  events: EligibilityChangeEvent[];
  totalCount: number;
}

interface EmployeeGroup {
  employeeId: number;
  employeeName: string;
  clientId: number;
  clientName: string;
  events: EligibilityChangeEvent[];
  latestEvent: EligibilityChangeEvent;
}

function groupByEmployee(events: EligibilityChangeEvent[]): EmployeeGroup[] {
  const map = new Map<number, EligibilityChangeEvent[]>();
  for (const e of events) {
    const arr = map.get(e.employeeId) || [];
    arr.push(e);
    map.set(e.employeeId, arr);
  }
  return Array.from(map.entries()).map(([employeeId, evts]) => ({
    employeeId,
    employeeName: evts[0].employeeName,
    clientId: evts[0].clientId,
    clientName: evts[0].clientName,
    events: evts,
    latestEvent: evts[0],
  }));
}

function getEventIcon(eventType: string, isEligible: boolean | null) {
  switch (eventType) {
    case "status_change":
      return isEligible ? (
        <UserPlus className="h-4 w-4 text-green-500" />
      ) : (
        <UserMinus className="h-4 w-4 text-red-500" />
      );
    case "calculated":
      return <UserCheck className="h-4 w-4 text-blue-500" />;
    case "enrolled":
      return <UserCheck className="h-4 w-4 text-green-500" />;
    case "terminated":
      return <UserMinus className="h-4 w-4 text-red-500" />;
    case "waived":
      return <UserMinus className="h-4 w-4 text-yellow-500" />;
    case "qle":
      return <Clock className="h-4 w-4 text-purple-500" />;
    case "dependent_added":
      return <UserPlus className="h-4 w-4 text-blue-500" />;
    case "dependent_enrolled":
      return <UserCheck className="h-4 w-4 text-green-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
}

function getEventBadge(eventType: string, isEligible: boolean | null) {
  switch (eventType) {
    case "status_change":
      return isEligible ? (
        <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
          Gaining Eligibility
        </Badge>
      ) : (
        <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100">
          Losing Eligibility
        </Badge>
      );
    case "calculated":
      return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          New Hire
        </Badge>
      );
    case "enrolled":
      return (
        <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
          Enrolled
        </Badge>
      );
    case "terminated":
      return (
        <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100">
          Terminated
        </Badge>
      );
    case "waived":
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          Waived
        </Badge>
      );
    case "qle":
      return (
        <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-100">
          Life Event
        </Badge>
      );
    case "dependent_added":
      return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          Dependent Added
        </Badge>
      );
    case "dependent_enrolled":
      return (
        <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
          Dependent Enrolled
        </Badge>
      );
    default:
      return (
        <Badge variant="outline">{eventType}</Badge>
      );
  }
}

function getEventDescription(event: EligibilityChangeEvent): string {
  switch (event.eventType) {
    case "status_change":
      if (event.previousEmploymentType && event.employmentType) {
        return `${event.previousEmploymentType} → ${event.employmentType}`;
      }
      return event.isEligible ? "Now eligible for benefits" : "No longer eligible";
    case "calculated":
      return "Eligibility calculated for new hire";
    case "enrolled":
      return event.notes || "Completed enrollment";
    case "terminated":
      return event.notes || "Coverage terminated";
    case "waived":
      return event.notes || "Waived coverage";
    case "qle":
      return event.notes || "Qualifying life event submitted";
    case "dependent_added":
      return event.notes || "Dependent added";
    case "dependent_enrolled":
      return event.notes || "Dependent enrolled";
    default:
      return event.notes || "Eligibility updated";
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function RecentEligibilityChanges() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
  const { can, isLoading: permissionsLoading } = usePermissions();
  const hasPermission = can("clients:view");
  const { toast } = useToast();

  const { data, isLoading } = useQuery<EligibilityChangesResponse>({
    queryKey: ["/api/agency/eligibility-changes", { limit: 5, days: 30 }],
    queryFn: async () => {
      const response = await fetch("/api/agency/eligibility-changes?limit=5&days=30");
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: hasPermission,
  });

  // Fetch all changes when dialog is open
  const { data: allChangesData, isLoading: allChangesLoading } = useQuery<EligibilityChangesResponse>({
    queryKey: ["/api/agency/eligibility-changes", { limit: 100, days: 90 }],
    queryFn: async () => {
      const response = await fetch("/api/agency/eligibility-changes?limit=100&days=90");
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: dialogOpen && hasPermission,
  });

  const sendReminderMutation = useMutation({
    mutationFn: async ({ employeeId, planId }: { employeeId: number; planId: number }) => {
      const res = await apiRequest("POST", "/api/reminders/send", { employeeId, planId });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Reminder Sent",
        description: data.message || "Enrollment reminder sent successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Send Reminder",
        description: error.message || "Could not send the reminder.",
        variant: "destructive",
      });
    },
  });

  // Don't render if user doesn't have permission
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Recent Eligibility Changes
          </CardTitle>
          <UserCheck className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const events = data?.events || [];
  const groups = groupByEmployee(events);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          Recent Eligibility Changes
        </CardTitle>
        <UserCheck className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-6">
            <UserCheck className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No recent changes</p>
            <p className="text-xs text-gray-400 mt-1">
              Eligibility changes will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div
                key={group.employeeId}
                className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="mt-0.5">
                  {getEventIcon(group.latestEvent.eventType, group.latestEvent.isEligible)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      href={`/clients/${group.clientId}/employees/${group.employeeId}`}
                      className="font-medium text-sm hover:text-primary hover:underline truncate"
                    >
                      {group.employeeName}
                    </Link>
                    {getEventBadge(group.latestEvent.eventType, group.latestEvent.isEligible)}
                    {group.events.length > 1 && (
                      <Badge variant="outline" className="text-xs">
                        +{group.events.length - 1} more
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {getEventDescription(group.latestEvent)}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{group.clientName}</span>
                    <span>•</span>
                    <span>
                      {formatDistanceToNow(new Date(group.latestEvent.createdAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  {group.latestEvent.effectiveDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Effective: {formatDate(group.latestEvent.effectiveDate)}
                      {group.latestEvent.enrollmentDueDate && (
                        <> | Due: {formatDate(group.latestEvent.enrollmentDueDate)}</>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Link
                    href={`/clients/${group.clientId}/employees/${group.employeeId}`}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="View details"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {events.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setDialogOpen(true)}
            >
              View All Changes ({data?.totalCount || 0})
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </CardContent>

      {/* All Eligibility Changes Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              All Eligibility Changes
              {allChangesData?.totalCount && (
                <Badge variant="secondary" className="ml-2">
                  {allChangesData.totalCount} total
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            {allChangesLoading ? (
              <div className="space-y-4 py-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2 py-2">
                {groupByEmployee(allChangesData?.events || []).map((group) => {
                  const isExpanded = expandedEmployees.has(group.employeeId);
                  const hasMultiple = group.events.length > 1;
                  return (
                    <div key={`dialog-group-${group.employeeId}`} className="rounded-lg border">
                      <div
                        className={`flex items-center gap-3 p-3 ${hasMultiple ? "cursor-pointer hover:bg-muted/50" : ""} transition-colors`}
                        onClick={() => {
                          if (!hasMultiple) return;
                          setExpandedEmployees((prev) => {
                            const next = new Set(prev);
                            next.has(group.employeeId) ? next.delete(group.employeeId) : next.add(group.employeeId);
                            return next;
                          });
                        }}
                      >
                        {hasMultiple && (
                          <div className="shrink-0">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link
                              href={`/clients/${group.clientId}/employees/${group.employeeId}`}
                              className="font-medium text-sm hover:text-primary hover:underline truncate"
                              onClick={(e) => { e.stopPropagation(); setDialogOpen(false); }}
                            >
                              {group.employeeName}
                            </Link>
                            <span className="text-xs text-muted-foreground">{group.clientName}</span>
                            {hasMultiple ? (
                              <Badge variant="secondary" className="text-xs">
                                {group.events.length} changes
                              </Badge>
                            ) : (
                              getEventBadge(group.latestEvent.eventType, group.latestEvent.isEligible)
                            )}
                          </div>
                          {!hasMultiple && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {getEventDescription(group.latestEvent)}
                              {group.latestEvent.effectiveDate && (
                                <> — Effective: {formatDate(group.latestEvent.effectiveDate)}</>
                              )}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Link
                            href={`/clients/${group.clientId}/employees/${group.employeeId}`}
                            onClick={() => setDialogOpen(false)}
                          >
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View details">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                      {hasMultiple && isExpanded && (
                        <div className="border-t px-3 pb-2">
                          {group.events.map((event) => (
                            <div
                              key={`dialog-${event.id}`}
                              className="flex items-start gap-3 py-2 pl-7 border-b last:border-b-0"
                            >
                              <div className="mt-0.5">
                                {getEventIcon(event.eventType, event.isEligible)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {getEventBadge(event.eventType, event.isEligible)}
                                  <span className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {getEventDescription(event)}
                                  {event.effectiveDate && (
                                    <> — Effective: {formatDate(event.effectiveDate)}</>
                                  )}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 shrink-0"
                                title={event.planId ? "Send reminder" : "No plan associated"}
                                disabled={!event.planId || sendReminderMutation.isPending}
                                onClick={() => event.planId && sendReminderMutation.mutate({
                                  employeeId: event.employeeId,
                                  planId: event.planId,
                                })}
                              >
                                <Bell className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(allChangesData?.events || []).length === 0 && (
                  <div className="text-center py-8">
                    <UserCheck className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No recent eligibility changes</p>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
